import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { Fragment, useEffect, useRef, useState, type CSSProperties } from "react";

import { useAuth } from "../lib/auth";
import { colorOfUser, contrastTextColor } from "../lib/color";
import { addComment, COMMENT_MAX, fetchComments, normalizeComment } from "../lib/media/comments";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";
import AvatarCircle from "./AvatarCircle";

/** 吹き出しに添える時刻 */
function timeOf(iso: string): string {
  const date = new Date(iso);
  return `${String(date.getHours())}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/** 日付の区切り。年をまたいだ古い会話だけ年も添える */
function dateLabelOf(iso: string): string {
  const date = new Date(iso);
  const weekday = "日月火水木金土"[date.getDay()] ?? "";
  const base = `${String(date.getMonth() + 1)}月${String(date.getDate())}日(${weekday})`;
  return date.getFullYear() === new Date().getFullYear()
    ? base
    : `${String(date.getFullYear())}年${base}`;
}

/**
 * 詳細表示のコメント欄。ふたりの会話として読めるよう、
 * 自分は右(テーマカラー)、相手は左(アイコン付き)の吹き出しで出す。
 * コメントは二人とも読める。書いたものは消せない(→ 20260815194433_drop_comment_delete.sql)。
 */
export default function MediaComments({ mediaId }: { mediaId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { session } = useAuth();
  const userId = session?.user.id;
  const [draft, setDraft] = useState("");

  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const comments = useQuery({
    queryKey: queryKeys.comments(mediaId),
    queryFn: () => fetchComments(mediaId),
  });

  // 会話は最新が下。開いたときと増えたときは末尾まで送る
  const listRef = useRef<HTMLElement | null>(null);
  const count = comments.data?.length ?? 0;
  useEffect(() => {
    const node = listRef.current;
    if (node !== null) node.scrollTop = node.scrollHeight;
  }, [count, mediaId]);

  const profileOf = (id: string) => profiles.data?.find((profile) => profile.id === id);

  const postComment = useMutation({
    mutationFn: async (raw: string) => {
      const body = normalizeComment(raw);
      if (body === null) return;
      await addComment(mediaId, body);
    },
    onSuccess: async () => {
      setDraft("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.comments(mediaId) });
      // タイル左下の件数バッジにも映す
      await queryClient.invalidateQueries({ queryKey: queryKeys.commentCounts });
      toast("コメントしました。");
    },
    onError: (e) => {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error("コメントに失敗", e);
      toast("コメントに失敗しました。", "error");
    },
  });

  return (
    <>
      {/* 高さは器(シート/サイドパネル)側で決まる。残りを全部使って一覧を出す */}
      <section
        ref={listRef}
        className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto overscroll-contain border-t border-white/15 px-3 py-3"
      >
        {comments.isPending && <p className="text-xs text-white/60">読み込み中...</p>}
        {comments.isError && (
          <p role="alert" className="text-xs text-white/60">
            コメントの取得に失敗しました。
          </p>
        )}
        {comments.isSuccess && comments.data.length === 0 && (
          <p className="text-xs text-white/40">コメントはまだありません。</p>
        )}

        {comments.data?.map((comment, i) => {
          const mine = comment.author_id === userId;
          const author = profileOf(comment.author_id);
          const bubbleColor = colorOfUser(profiles.data, comment.author_id, userId);
          const bubbleStyle = {
            "--bubble-color": bubbleColor,
            color: contrastTextColor(bubbleColor),
          } as CSSProperties;
          const prev = comments.data[i - 1];
          // 日付が変わる境目に区切りを置く(会話の頭にも)
          const newDay =
            prev === undefined ||
            new Date(prev.created_at).toDateString() !==
              new Date(comment.created_at).toDateString();
          return (
            <Fragment key={comment.id}>
              {newDay && (
                <div className="my-0.5 flex justify-center">
                  <span className="rounded-full bg-white/10 px-3 py-0.5 text-[10px] text-white/50">
                    {dateLabelOf(comment.created_at)}
                  </span>
                </div>
              )}
              {mine ? (
                <div className="flex items-end justify-end gap-1.5">
                  <span className="shrink-0 text-[10px] text-white/40">
                    {timeOf(comment.created_at)}
                  </span>
                  <span
                    style={bubbleStyle}
                    className="bubble-me max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line"
                  >
                    {comment.body}
                  </span>
                </div>
              ) : (
                <div className="flex items-end gap-1.5">
                  <AvatarCircle
                    avatar={author?.avatar}
                    name={author?.displayName}
                    color={bubbleColor}
                    className="size-7 shrink-0 self-start text-xs"
                  />
                  <span
                    style={bubbleStyle}
                    className="bubble-them max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line"
                  >
                    {comment.body}
                  </span>
                  <span className="shrink-0 text-[10px] text-white/40">
                    {timeOf(comment.created_at)}
                  </span>
                </div>
              )}
            </Fragment>
          );
        })}
      </section>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          postComment.mutate(draft);
        }}
        className="flex items-center gap-1 border-t border-white/15 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
      >
        <input
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          maxLength={COMMENT_MAX}
          placeholder="コメントを書く"
          autoComplete="off"
          data-1p-ignore
          // 16px 未満だと iOS が画面ごと拡大してしまう(index.css を参照)
          className="min-w-0 flex-1 rounded-full border border-white/30 bg-transparent px-4 py-2 text-base"
        />
        <button
          type="submit"
          disabled={postComment.isPending || normalizeComment(draft) === null}
          aria-label="送信"
          className="-m-1.5 flex size-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/15 disabled:opacity-30"
        >
          <Send className="size-5" aria-hidden />
        </button>
      </form>
    </>
  );
}
